// THE VR LOADING VIEW's PLACE (w59 vr-loading-view; owner 2026-11-03 "improve the loading screen, it should already
// give the feeling of mountains and valleys and lakes"; APP_STARTUP "The VR loading view"). Two pictures of REAL data
// the 2D Map already holds when a VR Beam starts (the session is asked from the Map) — nothing fetched, nothing invented:
//   the RELIEF   a table-top model of the destination valley: the Map's 16 m GROUND-M posts (mapheights.js) over a
//                window of at most RELIEF_WIN_M of the frame around the landing, coloured by the Map's own land-cover
//                picture (its underlay: the genome's class colours, the hillshade, water deep — maptiles.js MapClass),
//                RELIEF_M along its long side, heights × RELIEF_EXAG (a relief model's display convention; the heights
//                are the posts'), NORTH-ALIGNED with the world (so what lies ahead on the table lies ahead after the
//                landing); the landing point a cyan column. It rises from the floor (an object moving, never the view).
//   the SKYLINE  the ridges seen from the landing eye (ground + SKY_EYE_M): per azimuth the highest elevation angle
//                (earth curvature + refraction) of the posts — and of the far ring / horizon sampler (main.js farHf)
//                when phase 1 already has it — in three distance bands, drawn as layered silhouettes on the room's
//                horizon, the far band palest (aerial perspective into the background).
// Both are built in STEPS (placeSteps: a generator the loading view drains ≤ STEP_MS a frame) and drawn over the world
// like the rest of the room; the relief alone self-occludes: its depth is squeezed into the first 1 % of the buffer
// (NEAR_Z), always in front of the world drawn behind the room, never against it. Pure helpers are exported for the
// gate (check-mapfirst F9b).
import * as THREE from 'three';

export const RELIEF_M = 1.3;            // the model's long side (m in the room)
export const RELIEF_WIN_M = 12000;      // the window of the frame it shows (m, at most; clamped inside the frame)
export const RELIEF_EXAG = 1.5;         // vertical exaggeration (display only)
export const RELIEF_N = 192;            // grid vertices along the long side
export const RELIEF_AHEAD = 1.0;        // its centre ahead of the head's start (m)
export const RELIEF_DROP = 0.62;        // its base below the head's start height (m; ≥ RELIEF_MIN_Y above the floor)
export const RELIEF_MIN_Y = 0.45;
export const RELIEF_BASE = 0.025;       // the slab under the lowest post (m)
export const RISE_MS = 2600;            // the rise from the floor
export const SKY_N = 240;               // skyline azimuths
export const SKY_EYE_M = 1.7;
export const SKY_BANDS = [[60, 1500], [1500, 5000], [5000, 40000]];   // near, mid, far (m)
export const SKY_R = [26, 30, 34];      // their curtains' radius in the room (inside the dome's 40 m)
export const SKY_LOW = -0.14;           // the curtains' foot (rad below the horizon)
export const NEAR_Z = 0.01;
const R_EFF = 6371000 / 0.87;           // earth radius with the standard refraction (k 0.13)

// the window of the frame (W × H game m) the relief shows: ≤ max a side, centred on (x, z), clamped inside
export function reliefWindow(x, z, W, H, max = RELIEF_WIN_M) {
  const w = Math.min(W, max), h = Math.min(H, max);
  return { x0: Math.max(0, Math.min(W - w, x - w / 2)), z0: Math.max(0, Math.min(H - h, z - h / 2)), w, h };
}

// the posts' height at (x, z), bilinear (post (i, k) at ((i + 0.5) cell, (k + 0.5) cell), mapheights.js); null off them
export function postAt(P, x, z) {
  if (!P || P.w < 2 || P.h < 2) return null;
  const fx = x / P.cell - 0.5, fz = z / P.cell - 0.5;
  if (fx < -0.5 || fz < -0.5 || fx > P.w - 0.5 || fz > P.h - 0.5) return null;
  const i = Math.max(0, Math.min(P.w - 2, Math.floor(fx))), k = Math.max(0, Math.min(P.h - 2, Math.floor(fz)));
  const tx = Math.max(0, Math.min(1, fx - i)), tz = Math.max(0, Math.min(1, fz - k));
  const a = P.a, r0 = k * P.w + i, r1 = r0 + P.w;
  return (a[r0] * (1 - tx) + a[r0 + 1] * tx) * (1 - tz) + (a[r1] * (1 - tx) + a[r1 + 1] * tx) * tz;
}

// the skyline from eye {x, y, z} (game m): per band a Float32Array of SKY_N elevation angles (rad; -Infinity: no
// sample). Azimuth i: the bearing 2π i / n clockwise from NORTH (-z), x east. A generator: one azimuth a step
export function* skylineSteps(P, eye, { n = SKY_N, bands = SKY_BANDS, far = null } = {}) {
  const out = bands.map(() => new Float32Array(n).fill(-Infinity));
  const dMax = bands[bands.length - 1][1];
  for (let i = 0; i < n; i++) {
    const th = (2 * Math.PI * i) / n, dx = Math.sin(th), dz = -Math.cos(th);
    let b = 0;
    for (let d = bands[0][0]; d <= dMax; d += Math.max(P ? P.cell : 16, d * 0.015)) {
      while (b < bands.length - 1 && d > bands[b][1]) b++;
      const x = eye.x + dx * d, z = eye.z + dz * d;
      let y = postAt(P, x, z);
      if (y === null && far) y = far(x, z);
      if (y === null || !Number.isFinite(y)) continue;
      const e = Math.atan2(y - eye.y - (d * d) / (2 * R_EFF), d);
      if (e > out[b][i]) out[b][i] = e;
    }
    yield i;
  }
  return out;
}

// the relief's geometry over window win from posts P: positions in the room's metres (the window's centre at x = z = 0,
// the slab's foot at y = 0; x east, z south as the game's), uv into the window's picture (v = 1 north), aShade 1 on
// the surface, darker down the skirt; the landing point `at` (game x, z) → mark {x, y, z} on the surface; null: no posts
export function reliefGeometry(P, win, at, { n = RELIEF_N, size = RELIEF_M, exag = RELIEF_EXAG } = {}) {
  const long = Math.max(win.w, win.h), k = size / long;
  const nx = Math.max(8, Math.round((n * win.w) / long)), ny = Math.max(8, Math.round((n * win.h) / long));
  const hs = new Float32Array(nx * ny);
  let lo = Infinity, hi = -Infinity;
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const h = postAt(P, win.x0 + (win.w * i) / (nx - 1), win.z0 + (win.h * j) / (ny - 1));
      hs[j * nx + i] = h === null ? NaN : h;
      if (h !== null) { if (h < lo) lo = h; if (h > hi) hi = h; }
    }
  }
  if (!(hi >= lo)) return null;
  for (let q = 0; q < hs.length; q++) if (Number.isNaN(hs[q])) hs[q] = lo;
  const Y = (h) => RELIEF_BASE + (h - lo) * k * exag;
  const cx = win.x0 + win.w / 2, cz = win.z0 + win.h / 2;
  // the edge ring (clockwise from the NW corner): surface vertex index per edge post
  const ring = [];
  for (let i = 0; i < nx; i++) ring.push(i);
  for (let j = 1; j < ny; j++) ring.push(j * nx + nx - 1);
  for (let i = nx - 2; i >= 0; i--) ring.push((ny - 1) * nx + i);
  for (let j = ny - 2; j > 0; j--) ring.push(j * nx);
  const nv = nx * ny + 2 * ring.length;
  const pos = new Float32Array(nv * 3), uv = new Float32Array(nv * 2), sh = new Float32Array(nv);
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const v = j * nx + i;
      pos[3 * v] = (win.w * i / (nx - 1) - win.w / 2) * k;
      pos[3 * v + 1] = Y(hs[v]);
      pos[3 * v + 2] = (win.h * j / (ny - 1) - win.h / 2) * k;
      uv[2 * v] = i / (nx - 1); uv[2 * v + 1] = 1 - j / (ny - 1);
      sh[v] = 1;
    }
  }
  let v = nx * ny;
  for (const s of ring) {
    for (const [y, shade] of [[pos[3 * s + 1], 0.62], [0, 0.34]]) {
      pos[3 * v] = pos[3 * s]; pos[3 * v + 1] = y; pos[3 * v + 2] = pos[3 * s + 2];
      uv[2 * v] = uv[2 * s]; uv[2 * v + 1] = uv[2 * s + 1]; sh[v] = shade;
      v++;
    }
  }
  const idx = [];
  for (let j = 0; j < ny - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      const a = j * nx + i, b = a + 1, c = a + nx, d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }
  const r0 = nx * ny, rn = ring.length;
  for (let q = 0; q < rn; q++) {
    const a = r0 + 2 * q, b = r0 + 2 * ((q + 1) % rn);
    idx.push(a, a + 1, b, b, a + 1, b + 1);
  }
  const mx = (at.x - cx) * k, mz = (at.z - cz) * k, mh = postAt(P, at.x, at.z);
  return { pos, uv, sh, index: nv > 65535 ? new Uint32Array(idx) : new Uint16Array(idx), nx, ny, k, lo, hi,
    top: Y(hi), mark: { x: mx, y: Y(mh === null ? lo : mh), z: mz } };
}

// the window's picture: the Map's underlay (an ImageBitmap / canvas over wM × hM game m, row 0 = north) cropped to
// win into a canvas ≤ max px (the GPU's drawImage; the picture is a look, never exact data); null without one
export function reliefPicture(img, wM, hM, win, max = 1024) {
  if (!img || !(wM > 0) || !(hM > 0) || typeof document === 'undefined') return null;
  const sx = img.width / wM, sy = img.height / hM;
  const sw = win.w * sx, shh = win.h * sy, s = Math.min(1, max / Math.max(sw, shh));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(sw * s)); c.height = Math.max(1, Math.round(shh * s));
  const g = c.getContext('2d');
  if (!g) return null;
  g.drawImage(img, win.x0 * sx, win.z0 * sy, sw, shh, 0, 0, c.width, c.height);
  return c;
}

// the skyline's curtain for one band: per azimuth a top vertex at its angle (≥ SKY_LOW) and a foot at SKY_LOW, on a
// cylinder of radius r around the eye (x east, z south; uv.y 1 at the top)
export function skyCurtain(angles, r) {
  const n = angles.length, pos = new Float32Array((n + 1) * 2 * 3), uv = new Float32Array((n + 1) * 2 * 2), idx = [];
  for (let q = 0; q <= n; q++) {
    const i = q % n, th = (2 * Math.PI * i) / n, dx = Math.sin(th), dz = -Math.cos(th);
    const e = Math.max(SKY_LOW, angles[i]);
    for (const [s, ee] of [[0, e], [1, SKY_LOW]]) {
      const v = 2 * q + s;
      pos[3 * v] = dx * r * Math.cos(ee); pos[3 * v + 1] = r * Math.sin(ee); pos[3 * v + 2] = dz * r * Math.cos(ee);
      uv[2 * v] = q / n; uv[2 * v + 1] = s ? 0 : 1;
    }
    if (q < n) { const a = 2 * q; idx.push(a, a + 1, a + 2, a + 2, a + 1, a + 3); }
  }
  return { pos, uv, index: new Uint16Array(idx) };
}

const VS_NEAR = `attribute float aShade; varying vec2 vUv; varying float vS;
void main() { vUv = uv; vS = aShade; vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  p.z = -p.w + (p.z + p.w) * ${NEAR_Z}; gl_Position = p; }`;
const RELIEF_FS = `uniform float uO; uniform sampler2D map; uniform float uTex; varying vec2 vUv; varying float vS;
void main() {
  vec3 c = vS > 0.99 ? (uTex > 0.5 ? texture2D(map, vUv).rgb : vec3(0.55, 0.60, 0.52)) * vS : vec3(0.42, 0.39, 0.35) * (0.45 + 0.55 * vS / 0.62);
  gl_FragColor = vec4(c, uO);
}`;
const MARK_VS = `varying vec2 vUv; void main() { vUv = uv; vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  p.z = -p.w + (p.z + p.w) * ${NEAR_Z}; gl_Position = p; }`;
const MARK_FS = `uniform float uO; varying vec2 vUv;
void main() { gl_FragColor = vec4(0.404, 0.910, 0.976, uO * (0.25 + 0.75 * (1.0 - vUv.y))); }`;
const SKY_VS = 'varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }';
const SKY_FS = `uniform float uO; uniform vec3 uCol, uBg; varying vec2 vUv;
void main() { gl_FragColor = vec4(mix(uBg, uCol, smoothstep(0.0, 1.0, vUv.y) * 0.85 + 0.15), uO); }`;

// the place's meshes (three) from placeSteps' result, on `layer`, over the world: { sky: a Group around the eye (the
// curtains), table: a Group (the relief + the landing column; its foot at y 0), uR: their own opacity (the room's ×
// the place's fade-in), dispose() } — the loading view poses both (xrload.js XrLoad.update)
export function placeMeshes(res, { order, layer }) {
  const uR = { value: 0 };
  const mesh = (geo, mat, o) => {
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = o; m.frustumCulled = false; m.layers.enable(layer);
    return m;
  };
  const out = { uR, sky: null, table: null, tex: null };
  if (res.sky) {
    const sky = out.sky = new THREE.Group();
    sky.name = 'xrload.sky';
    res.sky.forEach((ang, b) => {
      const c = skyCurtain(ang, SKY_R[b]);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(c.pos, 3));
      g.setAttribute('uv', new THREE.BufferAttribute(c.uv, 2));
      g.setIndex(new THREE.BufferAttribute(c.index, 1));
      const m = new THREE.ShaderMaterial({ transparent: true, depthWrite: false, depthTest: false, fog: false, toneMapped: false,
        side: THREE.DoubleSide, vertexShader: SKY_VS, fragmentShader: SKY_FS,
        uniforms: { uO: uR, uCol: { value: new THREE.Vector3(...res.cols[b]) }, uBg: { value: new THREE.Vector3(...res.bg) } } });
      sky.add(mesh(g, m, order + 0.3 - b * 0.1));       // the far band first, the near one over it
    });
  }
  if (res.relief) {
    const R = res.relief, g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(R.pos, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(R.uv, 2));
    g.setAttribute('aShade', new THREE.BufferAttribute(R.sh, 1));
    g.setIndex(new THREE.BufferAttribute(R.index, 1));
    let tex = null;
    if (res.picture) {
      tex = out.tex = new THREE.CanvasTexture(res.picture);
      tex.colorSpace = THREE.NoColorSpace;     // raw sRGB bytes out, as the panel
      tex.anisotropy = 4;
    }
    const table = out.table = new THREE.Group();
    table.name = 'xrload.relief';
    table.add(mesh(g, new THREE.ShaderMaterial({ transparent: true, depthWrite: true, depthTest: true, fog: false, toneMapped: false,
      side: THREE.DoubleSide, vertexShader: VS_NEAR, fragmentShader: RELIEF_FS,
      uniforms: { uO: uR, map: { value: tex }, uTex: { value: tex ? 1 : 0 } } }), order + 1.5));
    const col = new THREE.CylinderGeometry(0.0035, 0.0035, 0.2, 8, 1, true);
    col.translate(0, 0.1, 0);
    const mk = mesh(col, new THREE.ShaderMaterial({ transparent: true, depthWrite: false, depthTest: true, fog: false, toneMapped: false,
      vertexShader: MARK_VS, fragmentShader: MARK_FS, uniforms: { uO: uR } }), order + 1.6);
    mk.position.set(R.mark.x, R.mark.y, R.mark.z);
    table.add(mk);
  }
  out.dispose = () => {
    for (const r of [out.sky, out.table]) {
      if (!r) continue;
      r.traverse((o) => { if (o.isMesh) { o.geometry.dispose(); o.material.dispose(); } });
      if (r.parent) r.parent.remove(r);
    }
    if (out.tex) out.tex.dispose();
  };
  return out;
}

// the colours (raw sRGB display values) of the skyline's bands near → far over the background bg [r, g, b]: the
// ridges' tone `ridge` washed toward bg with distance
export function skyColours(bg, ridge) {
  return [0, 0.42, 0.7].map((t) => ridge.map((c, i) => c + (bg[i] - c) * t));
}

// THE STEPS (the loading view drains them ≤ its STEP_MS a frame): the skyline's azimuths, then the relief + its
// picture. d = { posts, underlay: () => {img, wM, hM} | null (playermap.js), W, H, at: {x, y, z} (the landing
// ground), far, bg, ridge } → the place's data
export function* placeSteps(d) {
  const eye = { x: d.at.x, y: d.at.y + SKY_EYE_M, z: d.at.z };
  const it = skylineSteps(d.posts, eye, { far: d.far || null });
  let r;
  while (!(r = it.next()).done) yield 'sky';
  const sky = r.value;
  yield 'relief';
  const win = reliefWindow(d.at.x, d.at.z, d.W, d.H);
  const relief = d.posts ? reliefGeometry(d.posts, win, d.at) : null;
  yield 'picture';
  const u = relief && d.underlay ? d.underlay() : null;   // read NOW: a finer underlay closes the old bitmap
  const picture = u ? reliefPicture(u.img, u.wM, u.hM, win) : null;
  return { sky: sky.some((a) => a.some(Number.isFinite)) ? sky : null, relief, picture, win, bg: d.bg, cols: skyColours(d.bg, d.ridge) };
}
